import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';

import { Button as UiButton } from '@/components/ui/button';
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Dialog as UiDialog,
} from '@/components/ui/dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import { Schedule, ScheduleSelectionDialogProps } from './types';

export const ScheduleSelectionDialog = ({
  visible,
  onHide,
  schedules,
  selectedSchedule,
  onScheduleSelect,
  onCancel,
}: ScheduleSelectionDialogProps) => {
  const { t } = useTranslation();
  const isBaseUi = useFeatureFlag('schedule_selection_prime_to_base_ui');
  const formatAspirationDate = (dateString: string | null) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'MM/dd/yyyy');
    } catch (error) {
      return dateString || '';
    }
  };

  const handleSelect = () => {
    if (selectedSchedule) {
      onScheduleSelect(selectedSchedule);
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  if (isBaseUi) {
    return (
      <UiDialog
        open={visible}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
      >
        <DialogContent
          preflight={false}
          className="z-[2000] md:max-w-4xl"
          overlayClassName="z-[2000]"
        >
          <DialogHeader className="preflight">
            <DialogTitle>{t('scheduleSelection.title')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <DataTable
              value={schedules}
              selection={selectedSchedule}
              onSelectionChange={(e) => onScheduleSelect(e.value as Schedule)}
              selectionMode="single"
              dataKey="id"
              className="w-full"
            >
              <Column
                field="name"
                header={t('scheduleSelection.name')}
                sortable
              />
              <Column
                field="description"
                header={t('scheduleSelection.description')}
                sortable
              />
              <Column
                field="aspirationDate"
                header={t('scheduleSelection.aspirationDate')}
                sortable
                body={(rowData) => formatAspirationDate(rowData.aspirationDate)}
              />
              <Column
                field="smallFollicles"
                header={t('scheduleSelection.smallFollicles')}
                sortable
                body={(rowData) =>
                  (rowData.leftSmallFollicles || 0) +
                  (rowData.rightSmallFollicles || 0)
                }
              />
              <Column
                field="mediumFollicles"
                header={t('scheduleSelection.mediumFollicles')}
                sortable
                body={(rowData) =>
                  (rowData.leftMediumFollicles || 0) +
                  (rowData.rightMediumFollicles || 0)
                }
              />
              <Column
                field="largeFollicles"
                header={t('scheduleSelection.largeFollicles')}
                sortable
                body={(rowData) =>
                  (rowData.leftLargeFollicles || 0) +
                  (rowData.rightLargeFollicles || 0)
                }
              />
              <Column
                field="gradeAbc"
                header={t('scheduleSelection.gradeAbc')}
                sortable
              />
              <Column
                field="gradeD"
                header={t('scheduleSelection.gradeD')}
                sortable
              />
              <Column
                field="gradeE"
                header={t('scheduleSelection.gradeE')}
                sortable
              />
              <Column
                field="dead"
                header={t('scheduleSelection.dead')}
                sortable
              />
              <Column
                field="numberInMat"
                header={t('scheduleSelection.numberInMat')}
                sortable
              />
              <Column
                field="ivcNumber"
                header={t('scheduleSelection.ivcNumber')}
                sortable
              />
              <Column
                field="cleaved"
                header={t('scheduleSelection.cleaved')}
                sortable
              />
              <Column
                field="quality"
                header={t('scheduleSelection.quality')}
                sortable
              />
              <Column
                field="low"
                header={t('scheduleSelection.low')}
                sortable
              />
              <Column
                field="totalEmbryos"
                header={t('scheduleSelection.totalEmbryos')}
                sortable
              />
            </DataTable>
          </DialogBody>
          <DialogFooter className="preflight">
            <UiButton variant="outline" onClick={handleCancel}>
              {t('common.cancel')}
            </UiButton>
            <UiButton disabled={!selectedSchedule} onClick={handleSelect}>
              {t('common.select')}
            </UiButton>
          </DialogFooter>
        </DialogContent>
      </UiDialog>
    );
  }

  return (
    <Dialog
      header="Select Previous Schedule Masters"
      visible={visible}
      onHide={handleCancel}
      footer={
        <div className="flex justify-end gap-2">
          <Button label="Cancel" severity="secondary" onClick={handleCancel} />
          <Button
            label="Select"
            severity="success"
            disabled={!selectedSchedule}
            onClick={handleSelect}
          />
        </div>
      }
      className="w-full max-w-4xl"
    >
      <DataTable
        value={schedules}
        selection={selectedSchedule}
        onSelectionChange={(e) => onScheduleSelect(e.value as Schedule)}
        selectionMode="single"
        dataKey="id"
        className="w-full"
      >
        <Column field="name" header="Name" sortable />
        <Column field="description" header="Description" sortable />
        <Column
          field="aspirationDate"
          header="Aspiration Date"
          sortable
          body={(rowData) => formatAspirationDate(rowData.aspirationDate)}
        />
        <Column
          field="smallFollicles"
          header="Small Follicles"
          sortable
          body={(rowData) =>
            (rowData.leftSmallFollicles || 0) +
            (rowData.rightSmallFollicles || 0)
          }
        />
        <Column
          field="mediumFollicles"
          header="Medium Follicles"
          sortable
          body={(rowData) =>
            (rowData.leftMediumFollicles || 0) +
            (rowData.rightMediumFollicles || 0)
          }
        />
        <Column
          field="largeFollicles"
          header="Large Follicles"
          sortable
          body={(rowData) =>
            (rowData.leftLargeFollicles || 0) +
            (rowData.rightLargeFollicles || 0)
          }
        />
        <Column field="gradeAbc" header="Grade ABC" sortable />
        <Column field="gradeD" header="Grade D" sortable />
        <Column field="gradeE" header="Grade E" sortable />
        <Column field="dead" header="Dead" sortable />
        <Column field="numberInMat" header="Number In Mat" sortable />
        <Column field="ivcNumber" header="IVC Number" sortable />
        <Column field="cleaved" header="Cleaved" sortable />
        <Column field="quality" header="Quality" sortable />
        <Column field="low" header="Low" sortable />
        <Column field="totalEmbryos" header="Total Embryos" sortable />
      </DataTable>
    </Dialog>
  );
};
